/**
 * Count visible characters in LaTeX string
 * Excludes LaTeX commands and control sequences
 *
 * @param latex LaTeX string
 * @returns Number of visible characters
 */
// export function countVisibleCharsInLatex(latex: string, ignoreSpaces = false): number {
//   if (!latex) return 0;
//   // Remove LaTeX commands and control sequences
//   // This regex matches:
//   // - \command{...} style commands
//   // - \command style commands
//   // - Multiple spaces
//   let cleaned = latex
//     // Remove \command{...} style (including nested braces)
//     .replace(/\\[a-zA-Z]*\{[^}]*\}/g, '')
//     // Remove remaining \command style
//     .replace(/\\[a-zA-Z]*\s*/g, '')
//     // Remove special LaTeX characters that don't display
//     .replace(/[\{\}]/g, '')
//     // Remove multiple spaces (but keep single spaces as they might be meaningful)
//     .replace(/\s+/g, ' ');
//   if (ignoreSpaces) {
//     cleaned = cleaned.trim();
//   }
//   return cleaned.length;
// }
var extractBracedContent = function (str, startPos) {
    var depth = 1;
    var pos = startPos + 1;
    var content = '';
    while (pos < str.length && depth > 0) {
        if (str[pos] === '\\' &&
            pos + 1 < str.length &&
            (str[pos + 1] === '{' || str[pos + 1] === '}')) {
            content += str[pos] + str[pos + 1];
            pos += 2;
        }
        else if (str[pos] === '{') {
            depth++;
            content += '{';
            pos++;
        }
        else if (str[pos] === '}') {
            depth--;
            if (depth > 0)
                content += '}';
            pos++;
        }
        else {
            content += str[pos];
            pos++;
        }
    }
    return { content: content, endPos: pos - 1 };
};
// There is deliberately no `ignoreSpaces` parameter. An earlier signature took
// one, guarding an early return that compared `latex[i]` — a *single*
// character, always `"\\"` — against the two-character strings `"\\,"` /
// `"\\:"` / `"\\;"`, so it could never be true. Nothing was lost by removing
// it: the main loop never counts whitespace or braces, and LaTeX spacing
// commands already count 0 via the general backslash branch below. Restoring a
// working version of that short-circuit would be a behaviour *change*, not a
// fix — it would return 0 for a string like `\; a`, which visibly contains one
// character.
export var countVisibleCharsInLatex = function (latex) {
    if (!latex)
        return 0;
    var count = 0;
    var i = 0;
    var _loop_1 = function () {
        if (latex.substring(i).startsWith('\\frac')) {
            // Both arguments may be brace-less: MathLive drops the braces around any
            // single-token argument, so `\frac{1}{2}` serializes as `\frac12` and the
            // mixed forms `\frac1{23}` / `\frac{12}3` also occur. Matching only
            // `\frac{` sent `\frac12` down the generic \command branch, where it
            // counted 3 (`\frac` + `1` + `2`) instead of 1 — enough to trip a small
            // maxCharacters label, so a 1/12 fraction tile (limit 2) could not be
            // labelled `1/2` at all. Same normalisation the parser does in
            // MathEditor.tsx's parseNumericLatex.
            var pos_1 = i + 5;
            var readArg = function () {
                if (latex[pos_1] === '{') {
                    var braced = extractBracedContent(latex, pos_1);
                    pos_1 = braced.endPos + 1;
                    return countVisibleCharsInLatex(braced.content);
                }
                if (pos_1 >= latex.length)
                    return 0;
                if (latex[pos_1] === '\\') {
                    // A single command token, e.g. `\frac\pi2` — consume the whole name.
                    var cmdEnd = pos_1 + 1;
                    while (cmdEnd < latex.length && /[a-zA-Z]/.test(latex[cmdEnd])) {
                        cmdEnd++;
                    }
                    var token = latex.substring(pos_1, cmdEnd === pos_1 + 1 ? cmdEnd + 1 : cmdEnd);
                    pos_1 += token.length;
                    return countVisibleCharsInLatex(token);
                }
                var single = latex[pos_1];
                pos_1++;
                return countVisibleCharsInLatex(single);
            };
            var numCount = readArg();
            var denCount = readArg();
            count += Math.max(numCount, denCount);
            i = pos_1;
            return "continue";
        }
        if (latex[i] === '^') {
            i++;
            if (i < latex.length) {
                if (latex[i] === '{') {
                    var powerResult = extractBracedContent(latex, i);
                    var powerCount = countVisibleCharsInLatex(powerResult.content);
                    count += powerCount;
                    i = powerResult.endPos + 1;
                }
                else {
                    count += 1;
                    i++;
                }
            }
            return "continue";
        }
        if (latex[i] === '_') {
            i++;
            if (i < latex.length) {
                if (latex[i] === '{') {
                    var subResult = extractBracedContent(latex, i);
                    var subCount = countVisibleCharsInLatex(subResult.content);
                    count += subCount;
                    i = subResult.endPos + 1;
                }
                else {
                    count += 1;
                    i++;
                }
            }
            return "continue";
        }
        if (latex[i] === '\\') {
            var commandEnd = i + 1;
            while (commandEnd < latex.length && /[a-zA-Z]/.test(latex[commandEnd])) {
                commandEnd++;
            }
            var commandName = latex.substring(i + 1, commandEnd);
            // Non-alphabetic commands like \, \: \; \! \> (spacing) or \\ (newline)
            // \$ and \% are visible characters — count them as 1
            if (commandName === '' && commandEnd < latex.length) {
                var nextChar = latex[commandEnd];
                if (nextChar === '$' || nextChar === '%') {
                    count += 1;
                }
                i = commandEnd + 1; // skip the backslash + the next character
                return "continue";
            }
            // \placeholder{} counts as 1 visible box
            if (commandName === 'placeholder') {
                count += 1;
                if (commandEnd < latex.length && latex[commandEnd] === '{') {
                    var contentResult = extractBracedContent(latex, commandEnd);
                    i = contentResult.endPos + 1;
                }
                else {
                    i = commandEnd;
                }
                return "continue";
            }
            // Zero-width/invisible commands (spacing, phantoms)
            var invisibleCommands = ['quad', 'qquad', 'hspace', 'vspace', 'kern', 'phantom', 'hphantom', 'vphantom'];
            if (invisibleCommands.includes(commandName)) {
                if (commandEnd < latex.length && latex[commandEnd] === '{') {
                    var contentResult = extractBracedContent(latex, commandEnd);
                    i = contentResult.endPos + 1;
                }
                else {
                    i = commandEnd;
                }
                return "continue";
            }
            if (commandEnd < latex.length && latex[commandEnd] === '{') {
                var contentResult = extractBracedContent(latex, commandEnd);
                if (commandName === 'text') {
                    count += contentResult.content.length;
                }
                else {
                    var contentCount = countVisibleCharsInLatex(contentResult.content);
                    count += contentCount;
                }
                i = contentResult.endPos + 1;
            }
            else {
                count += 1;
                i = commandEnd;
            }
            return "continue";
        }
        // A bare `$` is a math-mode delimiter, not a visible character. MathLive
        // wraps its serialized value in `$ … $` whenever the field runs with
        // default-mode="text" (every manipulative label editor does), so counting
        // them inflated the total by 2 and made small maxCharacters limits (a 1/12
        // fraction tile allows 2) reject input that fits — MathEditor's input
        // handler then reverted the field to its previous value, so digits typed
        // into a fraction's numerator/denominator never stuck. The visible dollar
        // sign is `\$`, handled above.
        if (latex[i] === '$') {
            i++;
            return "continue";
        }
        if (!/[\s{}]/.test(latex[i])) {
            count++;
        }
        i++;
    };
    while (i < latex.length) {
        _loop_1();
    }
    return count;
};
